// frontend/src/admin/components/CreateVoucherModal.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";

import { createVoucher } from "../api";

/** Discount type → the label shown above the value input. */
const VALUE_LABEL = {
  percent: "Percent (%)",
  fixed_usd: "USD Amount ($)",
  free_months: "Number of Months",
};

const TYPE_OPTIONS = [
  { value: "percent", label: "Percent off" },
  { value: "fixed_usd", label: "Fixed USD amount" },
  { value: "free_months", label: "Free months" },
];

/**
 * CreateVoucherModal — form for minting a voucher. The value input's label
 * changes with the selected discount type (Percent / USD Amount / Months).
 */
function CreateVoucherModal({ onClose, onCreated }) {
  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState("percent");
  const [value, setValue] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        discount_type: discountType,
        discount_value: Number(value),
      };
      // Blank → the server auto-generates a strong, unguessable code.
      if (code.trim()) body.code = code.trim();
      if (maxRedemptions) body.max_redemptions = Number(maxRedemptions);
      if (notes.trim()) body.notes = notes.trim();
      const created = await createVoucher(body);
      toast.success(`Voucher ${created.code} created.`);
      onCreated?.(created);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create voucher.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          Create Voucher
        </h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label
              htmlFor="voucher-code"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Code (optional)
            </label>
            <input
              id="voucher-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Leave blank to auto-generate"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm uppercase"
            />
            <p className="mt-1 text-xs text-slate-500">
              Blank generates a strong, unguessable code. Set one only for a
              memorable campaign code (e.g. LAUNCH50).
            </p>
          </div>

          <div>
            <label
              htmlFor="voucher-type"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Discount type
            </label>
            <select
              id="voucher-type"
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="voucher-value"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              {VALUE_LABEL[discountType]}
            </label>
            <input
              id="voucher-value"
              type="number"
              min="1"
              step="1"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="voucher-max"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Max redemptions (optional)
            </label>
            <input
              id="voucher-max"
              type="number"
              min="1"
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
              placeholder="Unlimited"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="voucher-notes"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Notes (optional)
            </label>
            <input
              id="voucher-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-500 disabled:opacity-40"
            >
              {busy ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

CreateVoucherModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};
CreateVoucherModal.defaultProps = { onCreated: undefined };

export default CreateVoucherModal;
